import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const THANK_YOU_MSG_MAX_CHARS = 200;
const STAGE_COUNT = 10;
const MIN_ACTIVE_STAGES = 3;
const STAGE_TOGGLE_KEYS = Array.from({ length: STAGE_COUNT }, (_, i) => `stage${i + 1}`);

function parseStageToggle(raw) {
  return raw === "true" || raw === true;
}

function stageTogglesFromTheme(themeData) {
  return Object.fromEntries(
    STAGE_TOGGLE_KEYS.map((key) => [key, parseStageToggle(themeData?.[key] ?? "true")])
  );
}

function countActiveStageToggles(toggles) {
  return STAGE_TOGGLE_KEYS.filter((key) => toggles[key]).length;
}

/** Category order from theme payload (themeset + DB merge from fetchThemeData). */
function getThankyouCategoryKeys(themeData) {
  if (!themeData || typeof themeData !== "object") return [];
  const d = themeData.default_cat;
  const f = themeData.full_default_cat;
  if (Array.isArray(d) && d.length > 0) return d.map(String);
  if (Array.isArray(f) && f.length > 0) return f.map(String);
  const tm = themeData.thankyou_message;
  if (tm && typeof tm === "object" && !Array.isArray(tm)) {
    return Object.keys(tm);
  }
  return [];
}

/** Themeset + API only use plain strings per category (no score-band objects). */
function migrateThankyouMessageToCategoryStrings(raw, categoryKeys) {
  const o = Object.fromEntries(categoryKeys.map((k) => [k, ""]));
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return o;
  for (const k of categoryKeys) {
    if (typeof raw[k] === "string") o[k] = raw[k];
  }
  return o;
}

function clampThankyouCategoryStrings(msg, categoryKeys, maxLen) {
  const o = { ...msg };
  for (const k of categoryKeys) {
    if (typeof o[k] === "string" && o[k].length > maxLen) o[k] = o[k].slice(0, maxLen);
  }
  return o;
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, status, data } = useSelector((state) => state.theme);

  const [customTextThankYouPage, setCustomTextThankYouPage] = useState("");
  const [rules, setRules] = useState([""]);
  const [thankyouToggle, setThankyouToggle] = useState(false);
  const [thankyouMessage, setThankyouMessage] = useState({});
  const [textColor, setTextColor] = useState("#ffffff");
  const [buttonBackgroundColor, setButtonBackgroundColor] = useState("#6845D3");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [boxTextColor, setBoxTextColor] = useState("#000000");
  const [wrongPoints, setWrongPoints] = useState("1");
  const [stageToggles, setStageToggles] = useState(() => stageTogglesFromTheme(null));

  // Match Arrow Up: always load latest theme on Rules mount so a browser refresh
  // shows the saved values instead of local defaults.
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const categoryKeys = getThankyouCategoryKeys(data);
    setCustomTextThankYouPage(
      data.custom_text_thank_you_page != null ? String(data.custom_text_thank_you_page) : ""
    );
    setRules(Array.isArray(data.rules) && data.rules.length ? data.rules : [""]);
    setThankyouToggle(data.thankyou_toggle === "true" || data.thankyou_toggle === true);
    setThankyouMessage(
      clampThankyouCategoryStrings(
        migrateThankyouMessageToCategoryStrings(data.thankyou_message, categoryKeys),
        categoryKeys,
        THANK_YOU_MSG_MAX_CHARS
      )
    );
    setTextColor(data.text_color ?? "#ffffff");
    setButtonBackgroundColor(
      data.button_background_color ?? data.box_bg_color ?? "#6845D3"
    );
    setButtonTextColor(data.button_text_color ?? "#ffffff");
    setBoxTextColor(data.box_text_color ?? "#000000");
    setWrongPoints(String(data.wrong_points ?? "1"));
    setStageToggles(stageTogglesFromTheme(data));
  }, [data]);

  const categoryKeys = data ? getThankyouCategoryKeys(data) : [];
  const activeStageCount = countActiveStageToggles(stageToggles);
  const hasMinActiveStages = activeStageCount >= MIN_ACTIVE_STAGES;

  const showMinStagesPopup = () => {
    void Swal.fire({
      icon: "warning",
      title: "Minimum stages required",
      text: "At least 3 stages must be active.",
    });
  };

  const handleThankyouCategoryChange = (cat, val) => {
    if (val.length > THANK_YOU_MSG_MAX_CHARS) {
      void Swal.fire({
        icon: "warning",
        title: "Max limit crossed",
        text: `Thank you messages can be at most ${THANK_YOU_MSG_MAX_CHARS} characters each.`,
      });
      val = val.slice(0, THANK_YOU_MSG_MAX_CHARS);
    }
    setThankyouMessage((prev) => ({ ...prev, [cat]: val }));
  };

  const handleRuleChange = (idx, val) => {
    if (val.length > 200) {
      Swal.fire("Limit Exceeded", "Rules can be at most 200 characters.", "warning");
      val = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire("Limit Reached", "You can only add up to 6 rules.", "warning");
    }
    setRules([...rules, ""]);
  };

  const handleStageToggleChange = (key, checked) => {
    if (!checked && stageToggles[key] && activeStageCount <= MIN_ACTIVE_STAGES) {
      showMinStagesPopup();
      return;
    }
    setStageToggles((prev) => ({ ...prev, [key]: checked }));
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire("At Least One Rule Required", "You must have at least one rule.", "warning");
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    if (!hasMinActiveStages) {
      showMinStagesPopup();
      return;
    }

    const filledRules = rules.map((r) => r.trim()).filter(Boolean);
    if (filledRules.length === 0) {
      Swal.fire({
        title: "Validation",
        text: "Add at least one game rule. Rules cannot be left blank.",
        icon: "error",
      });
      return;
    }

    const msgOut = { ...thankyouMessage };
    for (const k of categoryKeys) {
      if (!(k in msgOut)) msgOut[k] = "";
    }

    const payload = {
      data: {
        custom_text_thank_you_page: customTextThankYouPage,
        rules: filledRules,
        thankyou_toggle: thankyouToggle ? "true" : "false",
        thankyou_message: msgOut,
        text_color: textColor,
        button_background_color: buttonBackgroundColor,
        button_text_color: buttonTextColor,
        box_bg_color: buttonBackgroundColor,
        box_text_color: boxTextColor,
        wrong_points: String(wrongPoints),
        ...Object.fromEntries(
          STAGE_TOGGLE_KEYS.map((key) => [key, stageToggles[key] ? "true" : "false"])
        ),
      },
      organizationId: organizationId || "admin",
      sessionId: sessionId || "admin",
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Saved", "Theme settings saved successfully.", "success");
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game rules
                </div>
                <h3 className="custom-rules-title">Rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <textarea
                      className="rule-input rule-textarea"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                      rows={3}
                    />
                    <button className="remove-btn" onClick={() => handleRemoveRule(i)}>
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <button className="add-btn" onClick={handleAddRule}>
                    + Add New Rule
                  </button>
                  <div className="code">
                    Max 6 rules. At least 1 required. 200 characters per rule.
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Colors
                </div>
                <div className="pallet-holder">
                  {[
                    { key: "text", label: "Text color", value: textColor, set: setTextColor },
                    {
                      key: "button_bg",
                      label: "Button background color",
                      value: buttonBackgroundColor,
                      set: setButtonBackgroundColor,
                    },
                    {
                      key: "button_text",
                      label: "Button text color",
                      value: buttonTextColor,
                      set: setButtonTextColor,
                    },
                    {
                      key: "box_text",
                      label: "Box text color",
                      value: boxTextColor,
                      set: setBoxTextColor,
                    },
                  ].map(({ key, label, value, set }) => (
                    <div className="pallet-row pallet-row--label-left" key={key}>
                      <label>{label}</label>
                      <input
                        type="color"
                        className="pallet-swatch"
                        value={value || "#000000"}
                        onChange={(e) => set(e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                <h3 className="custom-rules-title rules-stage-toggles-title">Stages</h3>
                <div className="rules-stage-toggles">
                  {STAGE_TOGGLE_KEYS.map((key, i) => (
                    <div className="form-group-v1 toogle-row" key={key}>
                      <label htmlFor={`rules-${key}`}>Stage {i + 1}</label>
                      <input
                        id={`rules-${key}`}
                        type="checkbox"
                        className="rules-stage-toggle-input"
                        checked={!!stageToggles[key]}
                        onChange={(e) => handleStageToggleChange(key, e.target.checked)}
                      />
                    </div>
                  ))}
                </div>
                {!hasMinActiveStages ? (
                  <p className="rules-stage-toggles-hint">
                    Minimum 3 stages must be on ({activeStageCount} active).
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button
              className="save-and-continue"
              onClick={handleSubmit}
              disabled={status === "loading" || !hasMinActiveStages}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
